import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import Header from '../../components/Header'
import { useTheme } from '../../contexts/ThemeContext'
import { useAuth } from '../../contexts/AuthContext'
import { coursesAPI, getApiBaseUrlWithoutApi } from '../../config/api'
import ExpiredSubscriptionModal from '../../components/ExpiredSubscriptionModal'
import { motion } from 'framer-motion'
import 'primeicons/primeicons.css'

interface Lesson {
  id: number
  title: string
  short_description?: string
  content?: string
  image?: string
  image_url?: string
  video_url?: string
  document_file?: string
  document_file_url?: string
  order: number
  is_active: boolean
  is_bonus?: boolean
}

interface Course {
  id: number
  title: string
  subtitle?: string
  description?: string
  image?: string
  lessons: Lesson[]
  regularLessons?: Lesson[]
  bonusLessons?: Lesson[]
  lesson_count?: number
  enrollment?: {
    progress: number
    completed_videos: number
    status: string
  }
}

export default function CourseDetailPage() {
  const { id } = useParams<{ id: string }>()
  const { theme } = useTheme()
  const { isAuthenticated } = useAuth()
  const navigate = useNavigate()
  const [course, setCourse] = useState<Course | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showExpiredModal, setShowExpiredModal] = useState(false)

  useEffect(() => {
    const fetchCourseDetail = async () => {
      if (!id || !isAuthenticated) return

      try {
        setLoading(true)
        setError(null)
        const data = await coursesAPI.getCourseDetail(parseInt(id))
        
        const API_BASE_URL = getApiBaseUrlWithoutApi()
        let imageUrl = data.image || '/images/team-photo-1-BB_Ia2hP.jpg'
        if (imageUrl && imageUrl.startsWith('/media/')) {
          imageUrl = `${API_BASE_URL}${imageUrl}`
        } else if (imageUrl && !imageUrl.startsWith('http') && !imageUrl.startsWith('/images/')) {
          imageUrl = `${API_BASE_URL}${imageUrl}`
        }

        // Process lessons
        const processedLessons = (data.lessons || []).map((lesson: any) => {
          let docUrl = lesson.document_file_url
          if (docUrl && docUrl.startsWith('/media/')) {
            docUrl = `${API_BASE_URL}${docUrl}`
          } else if (docUrl && !docUrl.startsWith('http') && !docUrl.startsWith('/images/')) {
            docUrl = `${API_BASE_URL}${docUrl}`
          }

          // Process image URL
          let lessonImageUrl = lesson.image_url || lesson.image
          if (lessonImageUrl && lessonImageUrl.startsWith('/media/')) {
            lessonImageUrl = `${API_BASE_URL}${lessonImageUrl}`
          } else if (lessonImageUrl && !lessonImageUrl.startsWith('http') && !lessonImageUrl.startsWith('/images/')) {
            lessonImageUrl = `${API_BASE_URL}${lessonImageUrl}`
          }

          return {
            ...lesson,
            document_file_url: docUrl,
            image_url: lessonImageUrl
          }
        }).sort((a: Lesson, b: Lesson) => a.order - b.order)

        // Separate regular lessons and bonus lessons
        const regularLessons = processedLessons.filter((l: Lesson) => !l.is_bonus)
        const bonusLessons = processedLessons.filter((l: Lesson) => l.is_bonus)

        setCourse({
          ...data,
          image: imageUrl,
          lessons: regularLessons, // Only regular lessons in main list
          regularLessons: regularLessons,
          bonusLessons: bonusLessons
        })
      } catch (error: any) {
        // Error fetching course detail
        
        if (error.response?.status === 403) {
          const errorMessage = error.response?.data?.error || ''
          if (errorMessage.includes('Kỳ hạn') || errorMessage.includes('hết hạn')) {
            const dismissedKey = `expired_subscription_modal_dismissed_/course/online/${id}`
            const wasDismissed = localStorage.getItem(dismissedKey)
            
            if (!wasDismissed) {
              setShowExpiredModal(true)
            }
          }
          setError(errorMessage || 'Bạn không có quyền truy cập khóa học này')
        } else if (error.response?.status === 404) {
          setError('Khóa học không tồn tại')
        } else {
          setError('Có lỗi xảy ra khi tải khóa học')
        }
      } finally {
        setLoading(false)
      }
    }

    if (isAuthenticated) {
      fetchCourseDetail()
    }
  }, [id, isAuthenticated])


  if (!isAuthenticated) {
    return (
      <main className={`min-h-screen flex items-center justify-center ${theme === 'dark' ? 'bg-black' : 'bg-gray-100'}`}>
        <div className="text-center">
          <p className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>Vui lòng đăng nhập để xem khóa học</p>
          <button
            onClick={() => navigate('/login')}
            className="mt-4 px-6 py-2 bg-gold-gradient text-black font-bold rounded-lg"
          >
            Đăng nhập
          </button>
        </div>
      </main>
    )
  }

  if (loading) {
    return (
      <main className={`min-h-screen flex items-center justify-center ${theme === 'dark' ? 'bg-black' : 'bg-gray-100'}`}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gold-400 mx-auto mb-4"></div>
          <p className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>Đang tải khóa học...</p>
        </div>
      </main>
    )
  }

  if (error || !course) {
    return (
      <main className={`min-h-screen ${theme === 'dark' ? 'bg-black' : 'bg-gray-100'}`}>
        <Header />
        <div className="container mx-auto px-4 py-20 text-center">
          <div className={`w-24 h-24 mx-auto mb-6 rounded-full flex items-center justify-center ${theme === 'dark' ? 'bg-red-500/10' : 'bg-red-100'}`}>
            <i className="pi pi-exclamation-triangle text-5xl text-red-400"></i>
          </div>
          <h2 className={`text-2xl font-bold mb-4 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
            {error || 'Không tìm thấy khóa học'}
          </h2>
          <button
            onClick={() => navigate('/course/my-courses')}
            className="px-6 py-2 bg-gold-gradient text-black font-bold rounded-lg"
          >
            Quay lại khóa học của tôi
          </button>
        </div>
      </main>
    )
  }

  return (
    <main className={`min-h-screen ${theme === 'dark' ? 'bg-black' : 'bg-gray-100'}`}>
      <Header />
      
      <ExpiredSubscriptionModal
        isOpen={showExpiredModal}
        onClose={() => setShowExpiredModal(false)}
      />

      {/* Course Header */}
      <section className="relative py-16 md:py-20 px-4 overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0 z-0">
          {course.image && (
            <img
              src={course.image}
              alt={course.title}
              className="w-full h-full object-cover"
            />
          )}
          <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/70 to-black/60"></div>
          <div className="absolute inset-0 bg-gradient-to-r from-gold-900/20 via-transparent to-gold-900/20"></div>
        </div>
        
        {/* Content */}
        <div className="relative z-10 container mx-auto max-w-6xl">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <button
              onClick={() => navigate('/course/my-courses')}
              className={`mb-6 flex items-center gap-2 ${theme === 'dark' ? 'text-gray-300 hover:text-gold-400' : 'text-gray-700 hover:text-gold-600'} transition-colors`}
            >
              <i className="pi pi-arrow-left"></i>
              <span>Quay lại</span>
            </button>
            
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-extrabold mb-4 text-white drop-shadow-2xl">
              <span className="bg-gradient-to-r from-gold-300 via-gold-400 to-gold-300 bg-clip-text text-transparent">
                {course.title}
              </span>
            </h1>
            {course.subtitle && (
              <p className="text-lg md:text-xl text-gray-200 mb-2 drop-shadow-lg">
                {course.subtitle}
              </p>
            )}
            
            {/* Course Stats */}
            <div className="flex flex-wrap gap-6 mt-6">
              <div className={`px-4 py-2 rounded-lg ${theme === 'dark' ? 'bg-gold-500/20' : 'bg-gold-100'} flex items-center gap-2`}>
                <i className="pi pi-book text-gold-400"></i>
                <span className={`font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                  {course.lessons.length} bài học
                </span>
              </div>
              {course.enrollment && (
                <div className={`px-4 py-2 rounded-lg ${theme === 'dark' ? 'bg-cyan-500/20' : 'bg-cyan-100'} flex items-center gap-2`}>
                  <i className="pi pi-chart-line text-cyan-400"></i>
                  <span className={`font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                    {Math.round(course.enrollment.progress)}% hoàn thành
                  </span>
                </div>
              )}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Lessons Section */}
      <section className={`py-12 md:py-16 px-4 ${theme === 'dark' ? 'bg-black' : 'bg-gray-100'}`}>
        <div className="container mx-auto max-w-6xl">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className={`text-2xl md:text-3xl font-bold mb-8 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}
          >
            Danh sách bài học
          </motion.h2>

          {course.lessons.length === 0 ? (
            <div className={`text-center py-12 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
              <i className="pi pi-book text-5xl mb-4 text-gold-400"></i>
              <p>Chưa có bài học nào trong khóa học này</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
              {course.lessons.map((lesson, index) => (
                <motion.div
                  key={lesson.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: index * 0.05 }}
                  whileHover={{ y: -4 }}
                  className={`group relative rounded-2xl overflow-hidden border ${
                    theme === 'dark' 
                      ? 'border-gold-500/30 bg-gradient-to-br from-gray-900/90 to-black hover:border-gold-500/50' 
                      : 'border-gold-200 bg-white hover:border-gold-400'
                  } shadow-lg ${theme === 'dark' ? 'shadow-gold-500/10' : 'shadow-gray-200'} hover:shadow-2xl ${theme === 'dark' ? 'hover:shadow-gold-500/20' : 'hover:shadow-gold-300/30'} transition-all duration-300 flex flex-col`}
                >
                  {/* Lesson Image - 16:9 aspect ratio */}
                  <div className="relative w-full overflow-hidden rounded-t-2xl" style={{ aspectRatio: '16/9' }}>
                    <img
                      src={lesson.image_url || course.image || '/images/team-photo-1-BB_Ia2hP.jpg'}
                      alt={lesson.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      onError={(e) => {
                        // Fallback to course image or default image
                        if (e.currentTarget.src !== (course.image || '/images/team-photo-1-BB_Ia2hP.jpg')) {
                          e.currentTarget.src = course.image || '/images/team-photo-1-BB_Ia2hP.jpg'
                        }
                      }}
                    />
                    {/* Gradient Overlay - Balanced */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent"></div>
                    
                    {/* Lesson Number Badge */}
                    <div className="absolute top-2 left-2 md:top-3 md:left-3">
                      <div className={`w-8 h-8 md:w-10 md:h-10 rounded-lg md:rounded-xl flex items-center justify-center font-bold text-base md:text-lg shadow-lg ${
                        theme === 'dark' ? 'bg-gold-500/95 text-black' : 'bg-gold-400 text-black'
                      }`}>
                        {index + 1}
                      </div>
                    </div>
                  </div>

                  {/* Lesson Content */}
                  <div className="p-3 md:p-4 flex flex-col flex-grow">
                    <h3 className={`text-base md:text-lg font-bold mb-1.5 line-clamp-2 ${theme === 'dark' ? 'text-white' : 'text-gray-900'} group-hover:text-gold-400 transition-colors`}>
                      {lesson.title}
                    </h3>
                    
                    {lesson.short_description && (
                      <p className={`text-xs md:text-sm mb-3 line-clamp-1 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                        {lesson.short_description}
                      </p>
                    )}

                    {/* Single Action Button - Xem bài tập */}
                    <button
                      onClick={() => navigate(`/course/online/${course.id}/lesson/${lesson.id}`)}
                      className={`w-full px-3 md:px-4 py-2 md:py-2.5 rounded-lg font-semibold text-xs md:text-sm flex items-center justify-center gap-2 transition-all ${
                        theme === 'dark'
                          ? 'bg-gold-gradient text-black hover:shadow-lg hover:shadow-gold-500/50'
                          : 'bg-gold-gradient text-black hover:shadow-lg hover:shadow-gold-400/50'
                      } transform hover:scale-105 active:scale-95`}
                    >
                      <span>XEM BÀI TẬP</span>
                      <i className="pi pi-arrow-right text-xs"></i>
                    </button>
                  </div>

                  {/* Hover Effect Glow */}
                  <div className={`absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none ${
                    theme === 'dark' ? 'bg-gradient-to-br from-gold-500/5 to-transparent' : 'bg-gradient-to-br from-gold-50/50 to-transparent'
                  }`}></div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Bonus Section */}
      {course.bonusLessons && course.bonusLessons.length > 0 && (
        <section className={`py-12 md:py-16 px-4 ${theme === 'dark' ? 'bg-black' : 'bg-gray-100'}`}>
          <div className="container mx-auto max-w-6xl">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center mb-8 md:mb-12"
            >
              <h2 className={`text-3xl md:text-4xl lg:text-5xl font-extrabold mb-4 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                <span className="bg-gradient-to-r from-gold-300 via-gold-400 to-gold-300 bg-clip-text text-transparent">
                  BONUS
                </span>
                <span className={theme === 'dark' ? 'text-white' : 'text-gray-900'}> ĐẶC BIỆT</span>
              </h2>
              <p className={`text-base md:text-lg ${theme === 'dark' ? 'text-white' : 'text-gray-700'}`}>
                Nội dung độc quyền dành cho học viên hoàn thành khóa học.
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
              {course.bonusLessons.map((lesson, index) => (
                <motion.div
                  key={lesson.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                  whileHover={{ y: -4 }}
                  className={`group relative rounded-2xl overflow-hidden border-2 ${
                    theme === 'dark' 
                      ? 'border-gold-500/50 bg-gradient-to-br from-gray-900/90 to-black hover:border-gold-500' 
                      : 'border-gold-400/50 bg-white hover:border-gold-500'
                  } shadow-lg ${theme === 'dark' ? 'shadow-gold-500/20' : 'shadow-gold-300/30'} hover:shadow-2xl ${theme === 'dark' ? 'hover:shadow-gold-500/40' : 'hover:shadow-gold-400/50'} transition-all duration-300 flex flex-col`}
                >
                  {/* Lesson Image - 16:9 aspect ratio */}
                  <div className="relative w-full overflow-hidden rounded-t-2xl" style={{ aspectRatio: '16/9' }}>
                    <img
                      src={lesson.image_url || course.image || '/images/team-photo-1-BB_Ia2hP.jpg'}
                      alt={lesson.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      onError={(e) => {
                        // Fallback to course image or default image
                        if (e.currentTarget.src !== (course.image || '/images/team-photo-1-BB_Ia2hP.jpg')) {
                          e.currentTarget.src = course.image || '/images/team-photo-1-BB_Ia2hP.jpg'
                        }
                      }}
                    />
                    {/* Gradient Overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent"></div>
                    
                    {/* BONUS Badge */}
                    <div className="absolute top-2 right-2 md:top-3 md:right-3">
                      <div className={`px-3 py-1.5 rounded-md ${
                        theme === 'dark' ? 'bg-gold-500/95' : 'bg-gold-400'
                      }`}>
                        <span className="text-black text-xs font-bold flex items-center gap-1">
                          <i className="pi pi-star text-xs"></i>
                          <span>BONUS</span>
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="relative p-5 md:p-6 flex flex-col flex-grow">
                    {/* Title */}
                    <h3 className={`text-lg md:text-xl font-bold mb-3 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                      {lesson.title}
                    </h3>
                    
                    {/* Description */}
                    {lesson.short_description && (
                      <p className={`text-sm md:text-base mb-6 flex-grow ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                        {lesson.short_description}
                      </p>
                    )}

                    {/* Action Button */}
                    <button
                      onClick={() => navigate(`/course/online/${course.id}/lesson/${lesson.id}`)}
                      className={`self-start flex items-center gap-2 font-semibold text-sm md:text-base transition-all ${
                        theme === 'dark' ? 'text-gold-400 hover:text-gold-300' : 'text-gold-600 hover:text-gold-700'
                      } group-hover:gap-3`}
                    >
                      <span>Xem ngay</span>
                      <i className="pi pi-arrow-right"></i>
                    </button>
                  </div>

                  {/* Hover Effect Glow */}
                  <div className={`absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none ${
                    theme === 'dark' ? 'bg-gradient-to-br from-gold-500/5 to-transparent' : 'bg-gradient-to-br from-gold-50/30 to-transparent'
                  }`}></div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

    </main>
  )
}
